import React, { useState, useEffect, useRef } from 'react';
import { Account, CreateTransferRequest, UpdateTransferRequest, Transfer, TransferType } from '../../apiServices/types';
import { transferService } from '../../apiServices/transferService';
import { formatCurrency } from '../../utils';
import { Modal, ConfirmationModal, GlassSelect, GlassSelectRef } from '../UI';
import './TransferModal.css';

interface TransferModalProps {
  isOpen: boolean;
  fromAccount: Account | null; // Може бути null, якщо не передано
  accounts: Account[];
  transfer?: Transfer | null;
  onClose: () => void;
  onSuccess: (updatedTransfer?: Transfer) => void;
}

const TransferModal: React.FC<TransferModalProps> = ({
  isOpen,
  fromAccount,
  accounts,
  transfer,
  onClose,
  onSuccess
}) => {
  const [fromAccountId, setFromAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [exchangeRate, setExchangeRate] = useState('');
  const [description, setDescription] = useState('');
  const [transferType, setTransferType] = useState<TransferType>(TransferType.Regular);
  const [isTransferTypeTouched, setIsTransferTypeTouched] = useState(false);
  const [date, setDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const selectRef = useRef<GlassSelectRef>(null);
  const fromAccountSelectRef = useRef<GlassSelectRef>(null);

  const isEditMode = !!transfer;

  // Функція для отримання дати у форматі YYYY-MM-DD
  const getDateOnly = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Функція для отримання сьогоднішньої дати у форматі YYYY-MM-DD
  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (isOpen) {
      if (transfer) {
        setIsLoading(true);
        setFromAccountId(transfer.fromAccountId);
        setToAccountId(transfer.toAccountId);
        setFromAmount(transfer.fromAmount.toString());
        setToAmount(transfer.toAmount?.toString() || '');
        setExchangeRate(transfer.exchangeRate?.toString() || '');
        setDescription(transfer.description || '');
        setTransferType(transfer.transferType);
        setDate(transfer.date ? getDateOnly(transfer.date) : getTodayDate());
        setIsLoading(false);
      } else {
        // При створенні нового трансферу
        if (fromAccount) {
          setFromAccountId(fromAccount.id);
        } else {
          setFromAccountId('');
        }
        setToAccountId('');
        setFromAmount('');
        setToAmount('');
        setExchangeRate('');
        setDescription('');
        setTransferType(TransferType.Regular);
        setDate(getTodayDate());
      }
      setIsTransferTypeTouched(false);
      setErrors({});
      setShowConfirmClose(false);
    }
  }, [isOpen, fromAccount, transfer]);

  const hasFormChanges = () => {
    if (!isEditMode) {
      return fromAccountId !== (fromAccount?.id || '') || fromAmount !== '' || toAmount !== '' || toAccountId !== '' || description !== '' || transferType !== TransferType.Regular || date !== getTodayDate();
    }
    
    if (!transfer) return false;
    
    const transferDateOnly = transfer.date ? getDateOnly(transfer.date) : getTodayDate();
    
    return (
      fromAccountId !== transfer.fromAccountId ||
      toAccountId !== transfer.toAccountId ||
      fromAmount !== transfer.fromAmount.toString() ||
      toAmount !== (transfer.toAmount?.toString() || '') ||
      exchangeRate !== (transfer.exchangeRate?.toString() || '') ||
      description !== (transfer.description || '') ||
      transferType !== transfer.transferType ||
      date !== transferDateOnly
    );
  };

  // Переказ зі звичайного рахунку на рахунок заощаджень за замовчуванням позначається як заощадження
  const handleToAccountChange = (value: string) => {
    setToAccountId(value);

    if (!isEditMode && !isTransferTypeTouched) {
      const fromAccountItem = accounts.find(account => account.id === fromAccountId);
      const toAccountItem = accounts.find(account => account.id === value);
      setTransferType(toAccountItem?.isSavings && !fromAccountItem?.isSavings ? TransferType.Savings : TransferType.Regular);
    }
  };

  const handleCloseAttempt = () => {
    if (hasFormChanges()) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const selectedFromAccount = accounts.find(acc => acc.id === fromAccountId);
  const availableToAccounts = accounts.filter(acc => acc.id !== fromAccountId);

  const calculateExchangeRate = () => {
    const from = parseFloat(fromAmount);
    const to = parseFloat(toAmount);
    
    if (from > 0 && to > 0) {
      const rate = from / to;
      setExchangeRate(rate.toFixed(4));
    }
  };

  const handleFromAmountChange = (value: string) => {
    setFromAmount(value);
    if (toAmount && value) {
      calculateExchangeRate();
    }
  };

  const handleToAmountChange = (value: string) => {
    setToAmount(value);
    if (fromAmount && value) {
      calculateExchangeRate();
    }
  };

  const handleExchangeRateChange = (value: string) => {
    setExchangeRate(value);
    if (fromAmount && value) {
      const rate = parseFloat(value);
      if (rate > 0) {
        const calculatedToAmount = parseFloat(fromAmount) / rate;
        setToAmount(calculatedToAmount.toFixed(2));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!fromAccountId) {
      newErrors.fromAccountId = 'Оберіть рахунок відправника';
    }

    if (!toAccountId) {
      newErrors.toAccountId = 'Оберіть рахунок отримувача';
    }

    if (fromAccountId && toAccountId && fromAccountId === toAccountId) {
      newErrors.toAccountId = 'Рахунок отримувача не може співпадати з рахунком відправника';
    }

    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      newErrors.fromAmount = 'Введіть суму більшу за нуль';
    } else if (selectedFromAccount && parseFloat(fromAmount) > selectedFromAccount.currentBalance) {
      newErrors.fromAmount = `Недостатньо коштів. Доступно: ${formatCurrency(selectedFromAccount.currentBalance)}`;
    }

    if (toAmount && parseFloat(toAmount) <= 0) {
      newErrors.toAmount = 'Сума отримувача повинна бути більшою за нуль';
    }

    if (exchangeRate && parseFloat(exchangeRate) <= 0) {
      newErrors.exchangeRate = 'Курс обміну повинен бути більшим за нуль';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !fromAccountId) return;

    try {
      setIsSubmitting(true);

      if (isEditMode && transfer) {
        const request: UpdateTransferRequest = {
          fromAccountId,
          toAccountId,
          fromAmount: parseFloat(fromAmount),
          toAmount: toAmount ? parseFloat(toAmount) : undefined,
          exchangeRate: exchangeRate ? parseFloat(exchangeRate) : undefined,
          description: description.trim() || '',
          transferType: transferType,
          date: date || undefined
        };

        const updatedTransfer = await transferService.update(transfer.id, request);
        onSuccess(updatedTransfer);
      } else {
        const request: CreateTransferRequest = {
          fromAccountId,
          toAccountId,
          fromAmount: parseFloat(fromAmount),
          toAmount: toAmount ? parseFloat(toAmount) : undefined,
          exchangeRate: exchangeRate ? parseFloat(exchangeRate) : undefined,
          description: description.trim() || '',
          transferType: transferType,
          date: date || undefined
        };

        await transferService.create(request);
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      setErrors({ general: error.message || (isEditMode ? 'Помилка оновлення трансферу' : 'Помилка створення трансферу') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      title={isEditMode ? 'Редагувати трансфер' : 'Створити трансфер'}
      onClose={handleCloseAttempt}
      size="md"
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCloseAttempt}
            disabled={isSubmitting || isLoading}
          >
            Скасувати
          </button>
          <button
            form="transfer-form"
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? (isEditMode ? 'Оновлення...' : 'Створення...') : (isEditMode ? 'Оновити трансфер' : 'Створити трансфер')}
          </button>
        </>
      }
    >
      <form id="transfer-form" onSubmit={handleSubmit} className="transfer-form">
        {isLoading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Завантаження...</p>
          </div>
        ) : (
          <>
        <div className="form-group">
          <label htmlFor="from-account">З рахунку</label>
          <GlassSelect
            ref={fromAccountSelectRef}
            value={fromAccountId}
            onChange={(value) => setFromAccountId(value)}
            options={accounts.map(account => ({
              value: account.id,
              label: `${account.name} (баланс: ${formatCurrency(account.currentBalance)})`
            }))}
            placeholder="Оберіть рахунок відправника"
            className={errors.fromAccountId ? 'error' : ''}
          />
          {errors.fromAccountId && (
            <span className="error-message">{errors.fromAccountId}</span>
          )}
        
        </div>

        <div className="form-group">
          <label htmlFor="to-account">На рахунок</label>
          <GlassSelect
            ref={selectRef}
            value={toAccountId}
            onChange={handleToAccountChange}
            options={availableToAccounts.map(account => ({
              value: account.id,
              label: `${account.name} (баланс: ${formatCurrency(account.currentBalance)})`
            }))}
            placeholder="Оберіть рахунок отримувача"
            className={errors.toAccountId ? 'error' : ''}
            disabled={!fromAccountId}
          />
          {errors.toAccountId && (
            <span className="error-message">{errors.toAccountId}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="transfer-type">Тип трансферу</label>
          <GlassSelect
            value={transferType}
            onChange={(value) => {
              setTransferType(value as TransferType);
              setIsTransferTypeTouched(true);
            }}
            options={[
              { value: TransferType.Regular, label: 'Звичайний переказ' },
              { value: TransferType.Savings, label: 'Заощадження' }
            ]}
            placeholder="Оберіть тип трансферу"
          />
        </div>

        <div className="form-group">
          <label htmlFor="transfer-date">Дата</label>
          <div className="date-input-wrapper">
            <input
              type="date"
              id="transfer-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="form-input date-input-custom"
              required
            />
            <label htmlFor="transfer-date" className="date-input-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="from-amount">Сума</label>
          <input
            type="number"
            id="from-amount"
            value={fromAmount}
            onChange={(e) => handleFromAmountChange(e.target.value)}
            className={errors.fromAmount ? 'form-input error' : 'form-input'}
            placeholder="0.00"
            required
            min="0.01"
            step="0.01"
          />
          {errors.fromAmount && (
            <span className="error-message">{errors.fromAmount}</span>
          )}
        </div>

        <div className="transfer-optional-section">
          <div className="optional-label">Додаткові параметри (для різних валют)</div>
          
          <div className="form-group">
            <label htmlFor="to-amount">Сума на рахунку отримувача</label>
            <input
              type="number"
              id="to-amount"
              value={toAmount}
              onChange={(e) => handleToAmountChange(e.target.value)}
              className={errors.toAmount ? 'form-input error' : 'form-input'}
              placeholder="Автоматично обчислиться"
              min="0.01"
              step="0.01"
            />
            {errors.toAmount && (
              <span className="error-message">{errors.toAmount}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="exchange-rate">Курс обміну</label>
            <input
              type="number"
              id="exchange-rate"
              value={exchangeRate}
              onChange={(e) => handleExchangeRateChange(e.target.value)}
              className={errors.exchangeRate ? 'form-input error' : 'form-input'}
              placeholder="Автоматично обчислиться"
              min="0.0001"
              step="0.0001"
            />
            {errors.exchangeRate && (
              <span className="error-message">{errors.exchangeRate}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Опис</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="form-input"
            placeholder="Додайте опис трансферу"
            rows={3}
          />
        </div>

        {errors.general && (
          <div className="error-message general-error">
            {errors.general}
          </div>
        )}
          </>
        )}
      </form>
    </Modal>

    <ConfirmationModal
      isOpen={showConfirmClose}
      title={isEditMode ? 'Скасувати редагування трансферу?' : 'Скасувати створення трансферу?'}
      message="У вас є незбережені зміни. Ви впевнені, що хочете закрити форму без збереження?"
      confirmText="Так"
      cancelText={isEditMode ? 'Ні' : 'Ні'}
      onConfirm={handleConfirmClose}
      onCancel={handleCancelClose}
      variant="warning"
    />
    </>
  );
};

export default TransferModal;

